import type { Visiteur } from "./navHeader";

/** État du compte (champ etat de /_api/license/status) ; « anonyme » sans session. */
export type EtatCompte = "anonyme" | "gratuit" | "essai" | "premium" | "impaye";

export type Compte = {
  visiteur: Visiteur;
  etat: EtatCompte;
  joursEssai: number | null;
  essaiUtilise: boolean;
  initiale: string;
  admin: boolean;
  /** Adresse du compte, pour le filigrane d'impression d'un abonné (partagePremium.ts). */
  email: string | null;
};

export const ANONYME: Compte = { visiteur: "anonyme", etat: "anonyme", joursEssai: null, essaiUtilise: false, initiale: "?", admin: false, email: null };

type Session = { user?: { displayName?: string | null; email?: string | null; role?: string | null } | null } | null;
type Licence = { hasFullAccess?: boolean; etat?: string; trialDaysRemaining?: number | null; hadTrial?: boolean } | null;

const ETATS: readonly EtatCompte[] = ["gratuit", "essai", "premium", "impaye"];

/** Compte déduit des deux réponses du serveur, sans appel réseau (testable). */
export function compteDepuis(session: Session, licence: Licence): Compte {
  const user = session?.user;
  if (!user) return ANONYME;
  const premium = licence?.hasFullAccess === true;
  const etatServeur = ETATS.find((e) => e === licence?.etat);
  const nom = (user.displayName || user.email || "?").trim();

  return {
    visiteur: premium ? "premium" : "gratuit",
    // Repli sans champ etat (réponse ancienne ou en erreur) : déduit de hasFullAccess.
    etat: etatServeur ?? (premium ? "premium" : "gratuit"),
    joursEssai: typeof licence?.trialDaysRemaining === "number" ? licence.trialDaysRemaining : null,
    essaiUtilise: licence?.hadTrial === true,
    initiale: nom.charAt(0).toUpperCase() || "?",
    admin: user.role === "admin",
    email: user.email ?? null,
  };
}

/** Une requête de session, puis de licence pour un connecté. Jamais d'erreur levée. */
export async function detectCompte(fetchFn: typeof fetch = fetch): Promise<Compte> {
  try {
    const session = await fetchFn("/_api/auth/session", { credentials: "same-origin" });
    if (!session.ok) return ANONYME;
    const s = (await session.json()) as Session;
    if (!s?.user) return ANONYME;
    const licence = await fetchFn("/_api/license/status", { credentials: "same-origin" });
    return compteDepuis(s, licence.ok ? ((await licence.json()) as Licence) : null);
  } catch {
    return ANONYME;
  }
}

/** Affiche l'élément d'état et les blocs du menu du compte ([data-etat], [data-km-…]). */
export function applyCompte(root: ParentNode, c: Compte): void {
  root.querySelectorAll<HTMLElement>("[data-etat]").forEach((el) => { el.hidden = el.dataset.etat !== c.etat; });
  root.querySelectorAll<HTMLElement>("[data-km-initiale]").forEach((el) => { el.textContent = c.initiale; });
  if (c.joursEssai !== null) {
    root.querySelectorAll<HTMLElement>("[data-km-essai-jours]").forEach((el) => { el.textContent = String(c.joursEssai); });
  }
  root.querySelectorAll<HTMLElement>("[data-km-essai-dispo]").forEach((el) => { el.hidden = c.essaiUtilise; });
  root.querySelectorAll<HTMLElement>("[data-km-essai-utilise]").forEach((el) => { el.hidden = !c.essaiUtilise; });
  root.querySelectorAll<HTMLElement>("[data-km-admin]").forEach((el) => { el.hidden = !c.admin; });
}

/** « 1 sur 7 hier », « 3 sur 7 ce soir » ou « 2 sur 6 le 14/10 » ; null sans grille ni tirage. */
export function scoreMonKeno(
  d: { configured?: boolean; matched?: unknown[] | null; playerNumbers?: unknown[] | null; tirageDate?: string | null; isToday?: boolean } | null,
  aujourdhui: Date = new Date(),
): string | null {
  if (!d?.configured || !Array.isArray(d.matched) || !Array.isArray(d.playerNumbers) || !d.tirageDate) return null;
  const base = `${d.matched.length} sur ${d.playerNumbers.length}`;
  if (d.isToday) return `${base} ce soir`;
  const hier = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth(), aujourdhui.getDate() - 1);
  const iso = `${hier.getFullYear()}-${String(hier.getMonth() + 1).padStart(2, "0")}-${String(hier.getDate()).padStart(2, "0")}`;
  if (d.tirageDate === iso) return `${base} hier`;
  const [, m, j] = d.tirageDate.split("-");
  return `${base} le ${j}/${m}`;
}

/** Ouverture du menu Mon espace, score de Mes grilles chargé à la première ouverture, déconnexion. */
export function initEspaceMenu(doc: Document, fetchFn: typeof fetch = fetch): void {
  const box = doc.querySelector<HTMLElement>("[data-km-me]");
  const btn = box?.querySelector<HTMLButtonElement>("[data-km-me-btn]");
  const menu = box?.querySelector<HTMLElement>(".km-me__menu");
  if (!box || !btn || !menu) return;
  let scoreCharge = false;

  const fermer = (rendreFocus = false) => {
    menu.hidden = true;
    btn.setAttribute("aria-expanded", "false");
    if (rendreFocus) btn.focus();
  };
  const ouvrir = () => {
    menu.hidden = false;
    btn.setAttribute("aria-expanded", "true");
    menu.querySelector<HTMLElement>("a, button")?.focus();
    if (scoreCharge) return;
    scoreCharge = true;
    void fetchFn("/_api/auth/my-draw-tonight", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const texte = scoreMonKeno(d);
        const cible = menu.querySelector<HTMLElement>("[data-km-mk-score]");
        if (cible && texte) cible.textContent = texte;
      })
      .catch(() => {});
  };

  btn.addEventListener("click", () => (menu.hidden ? ouvrir() : fermer()));
  box.addEventListener("keydown", (e) => { if (e.key === "Escape" && !menu.hidden) fermer(true); });
  doc.addEventListener("click", (e) => { if (!menu.hidden && !box.contains(e.target as Node)) fermer(); });

  menu.querySelector<HTMLButtonElement>("[data-km-logout]")?.addEventListener("click", () => {
    try { window.localStorage.removeItem("has_session"); } catch { /* stockage bloqué */ }
    void fetchFn("/_api/auth/logout", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: "{}" })
      .catch(() => {})
      .finally(() => window.location.assign("/"));
  });
}

/** Bouton « Mettre à jour ma carte » du bandeau d'impayé : portail Stripe, repli sur Mon compte. */
export function initPortail(doc: Document, fetchFn: typeof fetch = fetch): void {
  doc.querySelectorAll<HTMLButtonElement>("[data-km-portail]").forEach((btn) => {
    btn.addEventListener("click", () => {
      btn.disabled = true;
      void fetchFn("/_api/stripe/create-portal-session", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: "{}" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d: { url?: string } | null) => window.location.assign(d?.url || "/compte/"))
        .catch(() => window.location.assign("/compte/"));
    });
  });
}
